import type { InputParser } from "../types";

const formatDollars = (cents: number, trimZeroCents = false): string => {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: trimZeroCents ? 0 : 2,
    maximumFractionDigits: 2
  }).format(dollars);
};

const centsToEditable = (cents: number): string => {
  const num = cents / 100;
  if (num % 1 === 0) return String(num);
  return num.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

interface MoneyCentsOptions {
  asString?: boolean;
  max?: number;
  maxDigits?: number;
  trimZeroCents?: boolean;
}

function moneyCentsParser(): InputParser<number | null>;
function moneyCentsParser(opts: {
  asString: true;
  max?: number;
  maxDigits?: number;
  trimZeroCents?: boolean;
}): InputParser<string>;
function moneyCentsParser(opts?: MoneyCentsOptions): InputParser<number | null>;
function moneyCentsParser(
  opts?: MoneyCentsOptions
): InputParser<number | null> | InputParser<string> {
  const asString = opts?.asString ?? false;
  const max = opts?.max;
  const maxDigits = opts?.maxDigits;
  const trimZeroCents = opts?.trimZeroCents ?? false;

  const validate = (raw: string) => {
    if (raw === "") return true;
    if (!/^\d*\.?\d{0,2}$/.test(raw)) return false;
    if (maxDigits !== undefined) {
      const digits = raw.replace(/\D/g, "");
      if (digits.length > maxDigits) return false;
    }
    return true;
  };

  const clamp = (cents: number) => {
    if (max !== undefined && cents > max) return max;
    return cents;
  };

  if (asString) {
    return {
      inputMode: "decimal",
      validate,
      parse: (raw) => {
        if (raw === "") return "";
        const num = parseFloat(raw);
        if (isNaN(num)) return "";
        return String(clamp(Math.round(num * 100)));
      },
      format: (value: string) => {
        if (value === "") return "";
        const cents = parseInt(value, 10);
        if (isNaN(cents)) return "";
        return formatDollars(cents, trimZeroCents);
      },
      toEditable: (value: string) => {
        if (value === "") return "";
        const cents = parseInt(value, 10);
        if (isNaN(cents)) return "";
        return centsToEditable(cents);
      },
      cleanup: (raw) => raw.replace(/\.$/, "")
    } as InputParser<string>;
  }

  return {
    inputMode: "decimal",
    validate,
    parse: (raw) => {
      if (raw === "") return null;
      const num = parseFloat(raw);
      if (isNaN(num)) return null;
      return clamp(Math.round(num * 100));
    },
    format: (value: number | null) => {
      if (value === null) return "";
      return formatDollars(value, trimZeroCents);
    },
    toEditable: (value: number | null) => {
      if (value === null) return "";
      return centsToEditable(value);
    },
    cleanup: (raw) => raw.replace(/\.$/, "")
  };
}

export { moneyCentsParser };
